import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Copy, Trash2, X, Eye, EyeOff, Loader2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";

const CATEGORIES = ["Téléphones", "Accessoires", "Audio", "Chargeurs & câbles", "Protections", "Consoles & manettes", "Informatique", "Autres"];
const STATUSES = [
  { value: "available", label: "Disponible" },
  { value: "reserved", label: "Réservé" },
  { value: "sold", label: "Vendu" },
  { value: "hidden", label: "Masqué" },
];
const TAGS = [
  { value: "photo", label: "Photo" },
  { value: "autonomie", label: "Autonomie" },
  { value: "jeux", label: "Jeux" },
  { value: "stockage", label: "Stockage" },
];

const EMPTY = { name: "", category: "Téléphones", description: "", price: "", stock: 1, status: "available", images: [], specs: [], tags: [], brand: "", condition: "" };

function StatusPill({ status }) {
  const map = { available: "bg-emerald-100 text-emerald-700", reserved: "bg-amber-100 text-amber-700", sold: "bg-red-100 text-red-700", hidden: "bg-slate-200 text-slate-600" };
  const labels = { available: "Disponible", reserved: "Réservé", sold: "Vendu", hidden: "Masqué" };
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${map[status] || map.available}`}>{labels[status] || status}</span>;
}

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [editor, setEditor] = useState(null); // {id?} + form
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditor({ ...EMPTY });
  const openEdit = (p) => setEditor({ ...EMPTY, ...p });

  const save = async () => {
    if (!editor.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    const payload = { ...editor, price: parseFloat(editor.price) || 0, stock: parseInt(editor.stock, 10) || 0 };
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.demo;
    try {
      if (editor.id) {
        await api.put(`/admin/products/${editor.id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit créé");
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success("Produit dupliqué (masqué par défaut)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » définitivement ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produit supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleVisibility = async (p) => {
    try {
      const payload = { ...p, status: p.status === "hidden" ? "available" : "hidden" };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      await api.put(`/admin/products/${p.id}`, payload);
      toast.success(p.status === "hidden" ? "Produit publié" : "Produit masqué");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = (products || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  if (editor) {
    const set = (k, v) => setEditor((e) => ({ ...e, [k]: v }));
    return (
      <div data-testid="product-editor" className="mx-auto max-w-2xl">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{editor.id ? "Modifier le produit" : "Nouveau produit"}</h1>
          <button data-testid="editor-close-btn" onClick={() => setEditor(null)} className="rounded-full bg-slate-100 p-2.5 text-slate-500 hover:bg-slate-200"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-5 space-y-5">
          <div className="card-oxy p-5">
            <h2 className="font-display font-bold text-slate-900">Photos</h2>
            <p className="mb-3 mt-1 text-xs text-slate-500">La première photo est la photo principale.</p>
            <ImageUploader images={editor.images} onChange={(v) => set("images", v)} />
          </div>

          <div className="card-oxy space-y-4 p-5">
            <div>
              <label className="label-oxy">Nom du produit</label>
              <input data-testid="editor-name" value={editor.name} onChange={(e) => set("name", e.target.value)} className="input-oxy" placeholder="Ex : iPhone 13 128 Go" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-oxy">Catégorie</label>
                <select data-testid="editor-category" value={editor.category} onChange={(e) => set("category", e.target.value)} className="input-oxy">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-oxy">Statut</label>
                <select data-testid="editor-status" value={editor.status} onChange={(e) => set("status", e.target.value)} className="input-oxy">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label-oxy">Prix (€)</label>
                <input data-testid="editor-price" type="number" min="0" step="0.01" inputMode="decimal" value={editor.price} onChange={(e) => set("price", e.target.value)} className="input-oxy" />
              </div>
              <div>
                <label className="label-oxy">Stock</label>
                <input data-testid="editor-stock" type="number" min="0" step="1" inputMode="numeric" value={editor.stock} onChange={(e) => set("stock", e.target.value)} className="input-oxy" />
              </div>
              <div>
                <label className="label-oxy">Marque</label>
                <input data-testid="editor-brand" value={editor.brand} onChange={(e) => set("brand", e.target.value)} className="input-oxy" placeholder="Apple, Samsung…" />
              </div>
              <div>
                <label className="label-oxy">État</label>
                <select data-testid="editor-condition" value={editor.condition} onChange={(e) => set("condition", e.target.value)} className="input-oxy">
                  <option value="">—</option>
                  <option value="Neuf">Neuf</option>
                  <option value="Reconditionné">Reconditionné</option>
                  <option value="Occasion">Occasion</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-oxy">Description</label>
              <textarea data-testid="editor-description" rows={3} value={editor.description} onChange={(e) => set("description", e.target.value)} className="input-oxy resize-none" />
            </div>
            <div>
              <label className="label-oxy">Points forts (utilisés par le quiz)</label>
              <div className="flex flex-wrap gap-2">
                {TAGS.map((t) => (
                  <button key={t.value} type="button" data-testid={`editor-tag-${t.value}`}
                    onClick={() => set("tags", editor.tags.includes(t.value) ? editor.tags.filter((x) => x !== t.value) : [...editor.tags, t.value])}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${editor.tags.includes(t.value) ? "bg-ink text-gold" : "bg-slate-100 text-slate-600"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="card-oxy p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-slate-900">Caractéristiques</h2>
              <button type="button" data-testid="spec-add-btn" onClick={() => set("specs", [...editor.specs, { key: "", value: "" }])}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-200">
                <Plus className="h-3.5 w-3.5" /> Ajouter
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {editor.specs.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`spec-key-${i}`} value={s.key} onChange={(e) => set("specs", editor.specs.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
                    placeholder="Écran" className="input-oxy !py-2 text-sm" />
                  <input data-testid={`spec-value-${i}`} value={s.value} onChange={(e) => set("specs", editor.specs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                    placeholder="6,1&quot; OLED" className="input-oxy !py-2 text-sm" />
                  <button type="button" data-testid={`spec-delete-${i}`} onClick={() => set("specs", editor.specs.filter((_, j) => j !== i))}
                    className="shrink-0 rounded-xl bg-red-50 px-3 text-red-500 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {editor.specs.length === 0 && <p className="text-xs text-slate-400">Aucune caractéristique — ajoutez-en pour la fiche produit.</p>}
            </div>
          </div>

          <div className="sticky bottom-3 z-10 flex gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur">
            <button data-testid="editor-cancel-btn" onClick={() => setEditor(null)} className="btn-outline-oxy flex-1 !py-2.5 text-sm">Annuler</button>
            <button data-testid="editor-save-btn" onClick={save} disabled={saving} className="btn-gold flex-1 !py-2.5 text-sm disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Produits</h1>
        <button data-testid="product-create-btn" onClick={openCreate} className="btn-gold !px-5 !py-2.5 text-sm">
          <Plus className="h-4 w-4" /> Nouveau produit
        </button>
      </div>

      <div className="relative mt-5">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="input-oxy !pl-11" />
      </div>

      {products === null ? (
        <div className="mt-5 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="products-empty">
          <p className="font-display font-semibold text-slate-800">Aucun produit</p>
          <p className="mt-1 text-sm text-slate-500">Ajoutez votre premier produit pour remplir le catalogue.</p>
        </div>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="products-list">
          {filtered.map((p) => (
            <li key={p.id} data-testid={`product-row-${p.id}`} className="card-oxy flex items-center gap-4 p-3 sm:p-4">
              {p.images?.[0] ? (
                <img src={fileUrl(p.images[0])} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[10px] text-slate-400">Sans photo</div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-bold text-slate-900">{p.name}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>{p.category}</span> · <span className="font-bold text-ink">{p.price} €</span> · <span>Stock : {p.stock}</span>
                </p>
                <div className="mt-1.5"><StatusPill status={p.status} /></div>
              </div>
              <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
                <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} className="rounded-xl bg-slate-100 p-2.5 text-slate-600 hover:bg-gold-soft hover:text-gold-dark" title="Modifier"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`product-toggle-${p.id}`} onClick={() => toggleVisibility(p)} className="rounded-xl bg-slate-100 p-2.5 text-slate-600 hover:bg-gold-soft hover:text-gold-dark" title={p.status === "hidden" ? "Publier" : "Masquer"}>
                  {p.status === "hidden" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button data-testid={`product-duplicate-${p.id}`} onClick={() => duplicate(p)} className="rounded-xl bg-slate-100 p-2.5 text-slate-600 hover:bg-gold-soft hover:text-gold-dark" title="Dupliquer"><Copy className="h-4 w-4" /></button>
                <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p)} className="rounded-xl bg-slate-100 p-2.5 text-slate-600 hover:bg-red-100 hover:text-red-600" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
